import { Injectable } from '@angular/core';
import { IIDolRiferimentiUiStore } from './iidOlRiferimentiUi.store';
import { IID_OL_RIFERIMENTI_UI } from '@common/interfaces/interfaceData';
import { ActionService } from '../action/action.service';
import { Topics } from '@common/interfaces/topics';
import { IIDolRiferimentiUiQuery } from './iidOlRiferimentiUi.query'
import { CrudService } from '../crudService';
import { ProjectEntityStoreStrategy } from '../updateStoreStrategies';
import { WsACKCallback } from '@common/api/ack';
import { IBuildingUnits } from '@common/interfaces/gisfo';
import { RealtimeFeatureCommandService } from '@ep-om/realtime-v2';
import { InterfaceDataService } from '../interfaceDataRegistry';
import { InterfaceName } from '@common/interfaces/issueTypeInterface';

const TOPIC = Topics.IID_OL_RIFERIMENTI_UI
@Injectable({
  providedIn: 'root'
})
@InterfaceDataService(InterfaceName.OLO_RIFERIMENTI_UI)
export class IIDolRiferimentiUiService extends CrudService<IID_OL_RIFERIMENTI_UI, IIDolRiferimentiUiStore, IIDolRiferimentiUiQuery> {
  constructor(
    protected store: IIDolRiferimentiUiStore,
    public query: IIDolRiferimentiUiQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectEntityStoreStrategy,
    private featureCommandService: RealtimeFeatureCommandService,
  ) {
    super(
      TOPIC,
      store,
      query,
      actionService,
      updateStrategy
    );
  }

  refreshFromGisfo(id: string, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:refresh',
        dto: { id, updateEntity: true },
        ...this.projectIdFromEntity(id),
      },
      () => this.actionService.sendMessage(TOPIC, 'refresh', { id }, callback),
      callback,
    );
  }

  updateUi(dto: { id: string, payload: IBuildingUnits, updateEntity: boolean }, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:update',
        dto,
        ...this.projectIdFromEntity(dto.id),
      },
      () => this.actionService.sendMessage(TOPIC, 'update_ui', dto, callback),
      callback,
    );
  }

  createUi(dto: { id: string, payload: IBuildingUnits }, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:create',
        dto,
        ...this.projectIdFromEntity(dto.id),
      },
      () => this.actionService.sendMessage(TOPIC, 'create_ui', dto, callback),
      callback,
    );
  }

  deleteUi(dto: { id: string, pkBuildingUnits: string }, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:delete',
        dto,
        ...this.projectIdFromEntity(dto.id),
      },
      () => this.actionService.sendMessage(TOPIC, 'delete_ui', dto, callback),
      callback,
    );
  }

  associateUi(dto: { issueId: string, pkBuildingUnit: string | number }, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    const commandDto = { issueId: dto.issueId, pkBuildingUnit: Number(dto.pkBuildingUnit) };
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:associate',
        dto: commandDto,
        ...this.projectIdFromIssue(dto.issueId),
      },
      () => this.actionService.sendMessage(TOPIC, 'associate_ui', dto, callback),
      callback,
    );
  }

  associateSingleContract(dto: { issueId: string, pkBuildingUnit: string | number, contractId: string | number }, callback?: WsACKCallback<IID_OL_RIFERIMENTI_UI>) {
    const commandDto = {
      issueId: dto.issueId,
      pkBuildingUnit: Number(dto.pkBuildingUnit),
      contractId: Number(dto.contractId),
    };
    this.featureCommandService.send<IID_OL_RIFERIMENTI_UI>(
      {
        target: 'feature:signal',
        command: 'ui:contract:associate',
        dto: commandDto,
        ...this.projectIdFromIssue(dto.issueId),
      },
      () => this.actionService.sendMessage(TOPIC, 'associate_contract', dto, callback),
      callback,
    );
  }

  private projectIdFromEntity(id: string): { projectId?: string } {
    const projectId = this.query.getEntity(id)?.projectId;
    return projectId ? { projectId } : {};
  }

  private projectIdFromIssue(issueId: string): { projectId?: string } {
    const projectId = this.query.getByIssueId(issueId)?.[0]?.projectId;
    return projectId ? { projectId } : {};
  }
}
